import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as teamActions from '@redux/modules/team'

import defaultFormState, { requiredFields } from '@models/team'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { ImageModel, TeamMemberModel, TeamModel, TeamRequestOptions, UserModel } from '@models/types'

export type GenerateUrlsOptions = {
  prefixRoute?: string,
  queryString?: string,
}

export const generateUrls = (team?: Partial<TeamModel>, options?: GenerateUrlsOptions) => {
  const { id } = team || {}
  const { prefixRoute = 'systemManager', queryString = '' } = options || {}

  return {
    editTeamDetailsUrl: `#/${prefixRoute}/teams/${id}/edit/details`,
    editTeamUrl: `#/${prefixRoute}/teams/${id}/edit/`,
    teamsIndexUrl: `#/${prefixRoute}/teams${queryString}`,
  }
}

type CreateTeamParams = {
  dispatch: AppDispatch,
  requestOptions?: TeamRequestOptions,
  teamParams: Partial<TeamModel>,
}

const createTeam = (params: CreateTeamParams) => {
  const { dispatch, teamParams, requestOptions } = params
  const { createTeam: createFn } = teamActions

  return dispatch(createFn(teamParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editTeamUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editTeamUrl,
        success,
      }
    }

    return response
  })
}

type UpdateTeamParams = {
  dispatch: AppDispatch,
  requestOptions?: TeamRequestOptions,
  team: TeamModel,
  teamParams: Partial<TeamModel>,
}

const updateTeam = (params: UpdateTeamParams) => {
  const {
    dispatch, team, teamParams, requestOptions,
  } = params
  const { updateTeam: updateFn } = teamActions

  const updatedParams = {
    id: team.id,
    ...teamParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type SetAsDefaultParams = {
  dispatch: AppDispatch,
  team: DeleteParams<TeamModel>,
}

const setAsDefault = (params: SetAsDefaultParams) => {
  const { dispatch, team } = params
  const { setAsDefault: setAsDefaultFn } = teamActions

  return dispatch(setAsDefaultFn(team))
}

type DeleteTeamParams = {
  dispatch: AppDispatch,
  team: DeleteParams<TeamModel>,
}

const deleteTeam = (params: DeleteTeamParams) => {
  const { dispatch, team } = params
  const { deleteTeam: deleteFn } = teamActions

  return dispatch(deleteFn(team)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { teamsIndexUrl } = generateUrls()

      return {
        data,
        redirectUrl: teamsIndexUrl,
        success,
      }
    }

    return response
  })
}

type DuplicateTeamParams = {
  team: TeamModel,
  teamParams: Partial<TeamModel>,
  dispatch: AppDispatch,
  generateUrlsOptions?: GenerateUrlsOptions,
  requestOptions?: TeamRequestOptions,
}

const duplicateTeam = (params: DuplicateTeamParams) => {
  const {
    team, teamParams, dispatch, generateUrlsOptions, requestOptions,
  } = params

  const { duplicateTeam: duplicateFn } = teamActions

  const updatedParams = {
    id: team.id,
    ...teamParams,
  }

  // @ts-expect-error TS2345
  return dispatch(duplicateFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      const { editTeamUrl } = generateUrls(data, generateUrlsOptions)

      return {
        data,
        redirectUrl: editTeamUrl,
        success,
      }
    }

    return response
  })
}

export function useRelations(team: Partial<TeamModel>) {
  const { cover_image_id, lead_agent_member_id } = team || {}

  const { images, teamMembers, users } = useSelector(reduxState => reduxState.entities)

  const coverImage: Partial<ImageModel> = cover_image_id ? images[cover_image_id] || {} : {}
  const lead_agent_member: Partial<TeamMemberModel> = lead_agent_member_id
    ? teamMembers[lead_agent_member_id] || {} : {}
  // @ts-expect-error TS2339
  const leadAgent: Partial<UserModel> = lead_agent_member ? users[lead_agent_member.user_id] || {} : {}

  return {
    coverImage,
    leadAgent,
  }
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTeamForm(
  team: Partial<TeamModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const teamForm = useForm(
    defaultFormState,
    { entity: team, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [team.id, team.cache_key],
  )

  return {
    ...teamForm,
  }
}

type Options = {
  generateUrlsOptions?: GenerateUrlsOptions,
}

function useTeam(initEntity: Partial<TeamModel> = {}, options: Options = {}) {
  // @ts-expect-error TS2322
  const { entity: team }: { entity: TeamModel } = useLatestEntity(initEntity, 'teams')

  const { generateUrlsOptions } = options

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.teams)

  const { coverImage, leadAgent } = useRelations(team)

  return {
    callbacks: {
      addTeam: () => launchModal({
        callbacks,
        modalKey: 'CreateTeamModal',
        payload: { team },
      }),
      createTeam: (teamParams: Partial<TeamModel>, entityOptions?: TeamRequestOptions) => (
        createTeam({ teamParams, dispatch, requestOptions: entityOptions })
      ),
      deleteTeam: () => deleteTeam({ dispatch, team }),
      duplicateTeam: (teamParams: Partial<TeamModel>, entityOptions?: TeamRequestOptions) => duplicateTeam({
        team, teamParams, dispatch, generateUrlsOptions, requestOptions: entityOptions,
      }),
      launchDuplicateTeamModal: () => launchModal({
        callbacks,
        modalKey: 'DuplicateTeamModal',
        payload: { team },
      }),
      setAsDefault: (teamParams: Partial<TeamModel>) => (
        setAsDefault({
          // @ts-expect-error TS2353
          team, teamParams, dispatch,
        })
      ),
      updateTeam: (teamParams: Partial<TeamModel>, entityOptions?: TeamRequestOptions) => (
        updateTeam({
          team, teamParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    coverImage,
    creating,
    deleting,
    leadAgent,
    loading,
    team,
    updating,
    urls: generateUrls(team, generateUrlsOptions),
  }
}

export default useTeam
